import type { Board } from "./[boardId].page";
import GridLayout, { Layout } from "react-grid-layout";
import { v4 as uuidv4 } from "uuid";
import { Button, theme, Modal, Input, Row, Col } from "antd";
import React, {
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import {
  CompassOutlined,
  FileMarkdownOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { DashboardV1State, DeepLinkType } from "pages/api/deep-links/types";
import { useApplicationContext } from "src/hooks/useApplication";
import { useScrollTop } from "src/hooks/useScrollTop";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import {
  DEFAULT_COLUMN_COUNT,
  DEFAULT_ROW_HEIGHT,
  DEFAULT_SPACING,
} from "../dashboards/DashboardViewer";
import { MarkdownViewer } from "./MarkdownViewer";
import { QuestionInit } from "./QuestionLoader";
import { Flex } from "src/components/Flex";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { ContentViews } from "../explore/lib/useContentView";
import { SearchState } from "src/search/types";
import { unserializeParams } from "src/search/lib/params-state-reducer";
import { VizIcon } from "src/components/VizIcon";
import { Duration } from "src/utils/date";
import { ToasterMessageType, useToasterContext } from "src/hooks/useToaster";
import { stringifyEqual } from "src/utils/objects";
import { Hover } from "src/components/Hover";
import { VzblCard } from "src/components/VzblCard";
import { TextTruncated } from "src/components/TextTruncated";

import {
  normalizeTag,
  QuestionQuery,
  useQuestionFilter,
} from "src/hooks/useQuestionFilter";
import { CollapsibleTags } from "src/components/CollapsibleTags";

export function BoardBody({ board }: { board: Board }) {
  const app = useApplicationContext();
  const { token } = theme.useToken();
  const spacing =
    board.link?.json.spacing === undefined
      ? DEFAULT_SPACING
      : board.link?.json.spacing;
  const layout = useMemo(
    () =>
      board.link?.json.layout.sort((a, b) => {
        // This mystery code is sorting the layout so that tooltips hover over
        // subsequent cards in the expected manner.
        // If this is inverted, or left off, there will be occasions where the
        // chart tooltip will fall behind the cards.
        if (a.y !== b.y) return b.y - a.y;
        return b.x - a.x; // this part doesn't really matter
      }) || [],
    [board.link?.json.layout]
  );

  const [scrollRef, scrollTop] = useScrollTop();
  const parentRef = useRef<HTMLDivElement>(null);

  const hasScrollbars =
    (scrollRef.current?.scrollHeight || 0) >
    (scrollRef.current?.clientHeight || 0);
  const scrollBarWidth = hasScrollbars
    ? (parentRef.current?.clientWidth || 0) -
      (scrollRef.current?.clientWidth || 0)
    : 0;

  const [arranging, setArranging] = useState(false);
  const arrange = useCallback(() => setArranging(true), []);

  const children = useMemo(
    () =>
      layout.map((item) => {
        return (
          <div
            key={item.i}
            style={{
              height: "100%",
              width: "100%",
              overflow: "visible",
            }}
          >
            <div
              style={{
                height: "100%",
                width: "100%",
                position: "relative",
                overflow: arranging ? "hidden" : "visible",
                pointerEvents: arranging ? "none" : "initial",
                boxShadow: arranging
                  ? `0 0 0 2px ${token.colorPrimary}`
                  : "initial",
                backgroundColor: arranging
                  ? board.link?.json.data[item.i].transparent
                    ? "transparent"
                    : board.link?.json.colorTheme === "alternate"
                    ? "transparent"
                    : token.colorBgLayout
                  : "initial",
                borderTopLeftRadius: arranging ? 8 : "initial",
                borderTopRightRadius: arranging ? 8 : "initial",
                borderBottomLeftRadius: arranging ? 8 : "initial",
                borderBottomRightRadius: arranging ? 0 : "initial",
              }}
            >
              {board.link?.json.data[item.i] ? (
                board.link?.json.data[item.i].type === DeepLinkType.EXPLORE ? (
                  <QuestionInit
                    board={board}
                    cardId={item.i}
                    colorTheme={board.link.json.colorTheme}
                    json={board.link.json}
                    onArrange={arrange}
                  />
                ) : (
                  <MarkdownViewer
                    cardId={item.i}
                    json={board.link.json}
                    colorTheme={board.link.json.colorTheme}
                    board={board}
                    onArrange={arrange}
                  />
                )
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    width: "100%",
                    overflow: "visible",
                    backgroundColor: token.colorBgLayout,
                  }}
                >
                  <LoadingOutlined />
                </div>
              )}
              {arranging && (
                <div
                  style={{
                    height: "100%",
                    position: "absolute",
                    width: "100%",
                    top: 0,
                    left: 0,
                    backgroundColor: `${token.colorPrimary}44`,
                    zIndex: 1000,
                  }}
                />
              )}
            </div>
          </div>
        );
      }),
    [
      arrange,
      arranging,
      board.id,
      board.globalState,
      board.seed,
      board.link?.json.data,
      board.link?.json.colorTheme,
      layout,
      token.colorBgContainer,
      token.colorBgLayout,
      token.colorPrimary,
    ]
  );

  const toaster = useToasterContext();
  const saveLayoutChange = useCallback(
    (layout: Layout[]) => {
      if (!arranging) return;
      if (!board.link) return;
      if (stringifyEqual(board.link.json.layout, layout)) return;

      board
        .save({
          id: board.link.id,
          name: board.link.name,
          description: board.link.description,
          scope: board.link.userId
            ? "private"
            : board.link.orgId
            ? "workspace"
            : "global",
          ...board.link.json,
          layout,
        })
        .then(() => {
          toaster.addMessage(ToasterMessageType.SUCCESS, {
            message: "Saved board layout",
          });
        });
    },
    [arranging, board, toaster]
  );

  // for the add button, when available
  const columnCount = board.link?.json.columnCount || DEFAULT_COLUMN_COUNT;
  const rowHeight = board.link?.json.rowHeight || DEFAULT_ROW_HEIGHT;
  const addWidth = `calc(33% - ${spacing / 2}px)`;
  const addHeight = 3 * (rowHeight + spacing / 2);

  return (
    <Fragment
      key={app.sidebar.isOpen ? "app-sidebar-open" : "app-sidebar-closed"}
    >
      <DynamicCanvas>
        {({ width: dynamicWidth, height: dynamicHeight }) => (
          <div
            ref={parentRef}
            style={{
              overflow: "hidden",
              width: dynamicWidth,
              height: dynamicHeight,
              borderRadius: token.borderRadiusLG,
              backgroundColor:
                board.link?.json.colorTheme === "alternate"
                  ? "transparent"
                  : token.colorBgContainer,
            }}
          >
            {scrollTop > 4 && (
              <div
                style={{
                  position: "absolute",
                  boxShadow: `inset 0 0px 1.5px ${token.colorBgBase}`,
                  backgroundColor: "transparent",
                  width: "100%",
                  zIndex: 1,
                  left: 0,
                  height: 1,
                }}
              />
            )}
            <div
              key={board.id}
              ref={scrollRef}
              id="dashboard-scroll-parent"
              style={{
                height: "100%",
                width: "100%",
                overflowX: "hidden",
                overflowY: "auto",
                position: "relative",
              }}
            >
              <GridLayout
                isDraggable={arranging}
                isResizable={arranging}
                layout={layout}
                cols={columnCount}
                margin={[spacing, spacing]}
                rowHeight={rowHeight}
                containerPadding={[spacing, spacing]}
                width={dynamicWidth - scrollBarWidth}
                onLayoutChange={saveLayoutChange}
                style={{
                  backgroundColor:
                    board.link?.json.colorTheme === "alternate"
                      ? "transparent"
                      : token.colorBgContainer,
                }}
              >
                {children}
              </GridLayout>
              {arranging && (
                <>
                  <div
                    style={{
                      position: "fixed",
                      bottom: 0,
                      left: "calc(50% - 150px)",
                      width: 300,
                      height: 56,
                      backgroundColor: token.colorBgMask,
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                    }}
                  >
                    <Flex style={{ height: "100%" }}>
                      <Button
                        type="primary"
                        onClick={() => setArranging(false)}
                        style={{
                          boxShadow: `0 1px 1px ${token.colorBorder}, 0 0 0 1px ${token.colorBgContainer}`,
                        }}
                      >
                        Done Arranging
                      </Button>
                    </Flex>
                  </div>
                  <div style={{ height: 40 }} />
                </>
              )}
              {board.userCanEdit && !arranging && (
                <div
                  style={{
                    width: "100%",
                    height: "auto",
                    paddingLeft: spacing,
                    paddingRight: spacing,
                    paddingBottom: spacing,
                    paddingTop: 0,
                  }}
                >
                  <div
                    style={{
                      width: addWidth,
                      height: addHeight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      paddingLeft: spacing,
                    }}
                  >
                    <NewCardPicker board={board} setArranging={setArranging} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DynamicCanvas>
    </Fragment>
  );
}

function NewCardPicker(props: {
  board: Board;
  setArranging: (arranging: boolean) => void;
}) {
  const toaster = useToasterContext();
  const orgId = useApplicationContext().org_id!;
  const { token } = theme.useToken();
  const { links } = useDeepLinks(DeepLinkType.EXPLORE);
  const { links: examples } = useDeepLinks(DeepLinkType.EXAMPLE);
  const [modalOpen, setModalOpen] = useState(false);
  const [arranging, setArranging] = useState(false);
  const columnCount =
    props.board.link?.json.columnCount || DEFAULT_COLUMN_COUNT;
  const addColCount = Math.floor(columnCount / 2);
  const addRowCount = 3 * 2;

  const staticCards = [
    {
      key: "markdown-card",
      name: "-- markdown html text card",
      label: (
        <Flex start centeredY gap={8}>
          <div style={{ paddingTop: 2 }}>
            <FileMarkdownOutlined />
          </div>
          <span>Add Markdown Card</span>
        </Flex>
      ),
      value: "markdown",
      disabled: false,
      state: undefined,
    },
    {
      key: "question-card",
      name: "-- explore question new empty card",
      label: (
        <Flex start centeredY gap={8}>
          <div style={{ paddingTop: 2 }}>
            <CompassOutlined />
          </div>
          <span>Add Blank Card</span>
        </Flex>
      ),
      value: "question",
      state: undefined,
    },
  ];
  const questions = useMemo(
    () => [
      {
        label: "Saved Questions",
        options: (links.data || [])
          .map((link) => {
            let vizValue = ContentViews.default;
            let state = {} as {
              params: SearchState;
              viz: { value: ContentViews; config: any };
            };
            try {
              const data = JSON.parse(link.state);
              const params = unserializeParams(data.serialized);
              const viz = Array.isArray(data.viz) ? data.viz.at(0) : undefined;
              if (viz?.value) vizValue = viz.value;
              state = { params, viz };
            } catch {}
            const disabled =
              (props.board.link?.json.layout.findIndex(
                (layout_) => layout_.i === link.id
              ) || -1) >= 0;
            return {
              key: "key" + link.id,
              name: link.name.toLowerCase(),
              icon: <VizIcon inactive={disabled} value={vizValue} size={15} />,
              label: (
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: 250,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {link.name}
                </span>
              ),
              value: link.id,
              disabled,
              state,
              tags: (link.group || "").split(",").filter(Boolean),
            };
          })
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || "")),
      },
      {
        label: "Examples",
        options: (examples.data || [])
          .map((link) => {
            let vizValue = ContentViews.default;
            let state = {} as {
              params: SearchState;
              viz: { value: ContentViews; config: any };
            };
            try {
              const params = link.json.params;
              const viz = Array.isArray(link.json.viz)
                ? link.json.viz.at(0)
                : undefined;
              if (viz?.value) vizValue = viz.value;
              state = { params, viz };
            } catch {}
            const disabled =
              (props.board.link?.json.layout.findIndex(
                (layout_) => layout_.i === link.id
              ) || -1) >= 0;
            return {
              key: "key" + link.id,
              name: link.name.toLowerCase(),
              icon: <VizIcon inactive={disabled} value={vizValue} size={15} />,
              label: (
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: 250,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {link.name}
                </span>
              ),
              value: link.id,
              disabled,
              state,
              tags: (link.group || "").split(",").filter(Boolean),
            } as QuestionQuery;
          })
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || "")),
      },
    ],
    [examples.data, links.data, props.board.link?.json.layout]
  );

  const {
    filteredQuestions,
    searchValue,
    toggleFilter,
    clearFilters,
    activeFilters,
    parseAndApplySearch,
  } = useQuestionFilter(questions);

  const entityTypesFromQuestions = useMemo(() => {
    return [
      ...new Set(
        questions
          .flatMap((question) => question.options || [])
          .flatMap((option) => option.state?.params?.select)
          .filter(Boolean)
      ),
    ].sort() as string[];
  }, [questions]);

  const vizTypesFromQuestions = useMemo(() => {
    return [
      ...new Set(
        questions
          .flatMap((question) => question.options || [])
          .map((option) => option.state?.viz?.value)
          .filter(Boolean)
      ),
    ].sort();
  }, [questions]);

  const tagsFromQuestions = useMemo(() => {
    return [
      ...new Set(
        questions
          .flatMap((question) => question.options || [])
          .flatMap((option) => option.tags)
          .filter(Boolean)
      ),
    ].sort() as string[];
  }, [questions]);

  const handleSelect = useCallback(
    (_: any, selection: any) => {
      const question = selection;
      if (!question) return;
      if (!props.board.link) return;

      const i = uuidv4();
      const maxY = props.board.link.json.layout.reduce(
        (a, c) => (c.y > a ? c.y : a),
        0
      );
      const newLayouts = [
        ...(props.board.link.json.layout || []),
        { i: i, x: 0, y: maxY + 1, w: addColCount, h: addRowCount },
      ];
      const newData: DashboardV1State["data"] = {
        ...props.board.link.json.data,
        [i]:
          question.state && question.name
            ? {
                type: DeepLinkType.EXPLORE,
                style: {},
                title: question.name,
                showTitle: true,
                centerTitle: false,
                transparent: false,
                state: question.state,
              }
            : question.key === "markdown-card"
            ? {
                type: "markdown",
                style: {},
                transparent: false,
                markdown: `# Markdown block\n---\nEdit this card to add your own content`,
              }
            : {
                type: DeepLinkType.EXPLORE,
                style: {},
                title: "New Question Card",
                showTitle: true,
                centerTitle: false,
                transparent: false,
                state: {
                  params: {
                    orgId,
                    duration: "1PD" as Duration,
                    select: ["Event"],
                  },
                  viz: { value: ContentViews.default, config: {} },
                },
              },
      };
      props.board
        .save({
          id: props.board.link.id,
          name: props.board.link.name,
          description: props.board.link.description,
          scope: props.board.link.userId
            ? "private"
            : props.board.link.orgId
            ? "workspace"
            : "global",
          ...props.board.link.json,
          data: newData,
          layout: newLayouts,
        })
        .then(() => {
          toaster.addMessage(ToasterMessageType.SUCCESS, {
            message: "Saved new card to board",
          });
          props.setArranging(true);
        });
      clearFilters();
      setModalOpen(false);
    },
    [addColCount, addRowCount, orgId, props.board, props.setArranging, toaster]
  );

  return (
    <>
      <Hover
        baseStyle={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 60,
          height: 60,
          backgroundColor: token.colorBgContainer,
          borderRadius: "50%",
          color: token.colorPrimary,
          cursor: "pointer",
          border: `1px solid ${token.colorBorder}`,
          transition: "all 0.2s ease",
          boxShadow: token.boxShadowTertiary,
        }}
        hoverStyle={{
          backgroundColor: token.colorPrimaryBg,
          transform: "scale(1.05)",
          boxShadow: token.boxShadowSecondary,
        }}
      >
        <div
          onClick={() => setModalOpen(true)}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PlusOutlined style={{ fontSize: 24 }} />
        </div>
      </Hover>

      {modalOpen && (
        <Modal
          open={true}
          onCancel={() => {
            clearFilters();
            setModalOpen(false);
          }}
          footer={null}
          title="Add Card"
          width={900}
          style={{
            maxHeight: "80vh",
            top: "10vh",
          }}
          bodyStyle={{
            height: "calc(80vh - 110px)",
            overflow: "hidden",
            padding: "16px",
          }}
        >
          <div
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Input.Search
              value={searchValue}
              onChange={(e) => parseAndApplySearch(e.target.value)}
              placeholder="Filter saved queries..."
              style={{ marginBottom: 16 }}
              size="large"
            />
            <Row style={{ flex: 1, overflow: "hidden" }}>
              <Col
                span={8}
                style={{
                  height: "100%",
                  overflowY: "auto",
                  paddingRight: 16,
                  borderRight: `1px solid ${token.colorBorder}`,
                }}
              >
                {staticCards.map((question) => (
                  <div key={question.key}>
                    <div
                      onClick={() => handleSelect(question.value, question)}
                      style={{ cursor: "pointer" }}
                    >
                      {question.label}
                    </div>
                  </div>
                ))}
                <div>
                  <CollapsibleTags
                    title="Entity"
                    tags={entityTypesFromQuestions}
                    activeFilters={activeFilters}
                    attribute="select"
                    onTagClick={(tag) =>
                      toggleFilter(`select:${normalizeTag(tag)}`)
                    }
                    colorPrimaryActive={token.colorPrimaryActive}
                  />

                  <CollapsibleTags
                    title="Tags"
                    tags={tagsFromQuestions}
                    activeFilters={activeFilters}
                    attribute="tag"
                    onTagClick={(tag) =>
                      toggleFilter(`tag:${normalizeTag(tag)}`)
                    }
                    colorPrimaryActive={token.colorPrimaryActive}
                  />
                  <CollapsibleTags
                    title="Visualization"
                    tags={vizTypesFromQuestions}
                    activeFilters={activeFilters}
                    attribute="visualization"
                    onTagClick={(tag) =>
                      toggleFilter(`visualization:${normalizeTag(tag)}`)
                    }
                    colorPrimaryActive={token.colorPrimaryActive}
                  />
                </div>
              </Col>
              <Col
                span={16}
                style={{
                  height: "100%",
                  overflowY: "auto",
                  paddingLeft: 16,
                }}
              >
                {filteredQuestions.map((question) => (
                  <div style={{ marginBottom: 30 }} key={question.label}>
                    <h3>
                      {question.label} ({question.options?.length})
                    </h3>
                    <div>
                      <Flex
                        wrap
                        gap={16}
                        style={{
                          width: "100%",
                          justifyContent: "flex-start",
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                        }}
                      >
                        {question.options &&
                          question.options.map((option) => (
                            <VzblCard
                              title={
                                <TextTruncated lines={2} lineHeight={"1.2em"}>
                                  {option.label}
                                </TextTruncated>
                              }
                              icon={option.icon}
                              key={option.key}
                              onClick={() =>
                                !option.disabled &&
                                handleSelect(option.value, option)
                              }
                              style={{
                                cursor: "pointer",
                                width: "calc(50% - 10px)",
                                opacity: option.disabled ? 0.5 : 1,
                              }}
                            />
                          ))}
                      </Flex>
                    </div>
                  </div>
                ))}
              </Col>
            </Row>
          </div>
        </Modal>
      )}
    </>
  );
}
